import React, { useEffect, useState, useContext } from "react";

//* MUI Components
import { styled, useTheme } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";

//* NDBX and Custom controls
import { OneColumnStack, Section } from "@arp/react-ndbx";
import CreditDebitCard from "./CreditDebitCard.jsx";
import RHFBankAccDetails from "./RHFBankAccDetails.jsx";
import SecureCoModal from "../../pageLevel/paymentPaymentDetails/SecureCoModal";
import CreditCardDetails from "../paymentPaymentDetails/CreditCardDetails.jsx";
import ToggleButtonNotification from "../../organisms/ToggleButtonNotification.jsx";
import PageLoadingBackdrop from "../../organisms/PageLoadingBackdrop";

//* Config
import {
  questionTypes,
  questionLabels,
  notificationCardItems,
} from "../../../config/questions/questionItems.js";
import { paymentDetailsFieldNames } from "../../../config/pageInfo/pageFieldName";
import { paymentDetailsNotification } from "../../../config/notifications/pageNotifications";

//* Context
import { PaymentContext } from "../../../context/BraintreeContext.jsx";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import { useOutletContext } from "react-router-dom";

//!----------------------------------------------------------------

const AccordionSummary = styled((props) => <MuiAccordionSummary {...props} />)(
  ({ theme, ...props }) => ({
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, .05)"
        : { ...props.backgroundColor },
    maxHeight: { ...props.maxHeight },

    "& .MuiAccordionSummary-content": {
      justifyContent: "left",
      flexGrow: 0,
      minWidth: { xs: "fit-content", md: 540 },
      outline: "0px solid red",
      marginLeft: { md: "-18px" },
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: { ...props.maxWidth },
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
    },
  })
);

const AccordionDetails = styled((props) => <MuiAccordionDetails {...props} />)(
  ({ theme, ...props }) => ({
    "&.MuiAccordionDetails-root": {
      paddingTop: { xs: 1, sm: 16 },
      paddingBottom: { xs: 1, sm: 16 },
      paddingLeft: 0,
      paddingRight: 0,
    },
  })
);

const AccordionContainer = styled((props) => <Accordion {...props} />)(
  ({ theme, ...props }) => ({
    "&.MuiAccordion-root": {
      "&:before": {
        backgroundColor: "#707070",
        opacity: 0.5,
      },
      "&.Mui-expanded:before": {
        opacity: 0.5,
      },
    },
  })
);

export default function AccordionRadioButton({
  children = [],
  sx = [],
  ...props
}) {
  if (Array.isArray(children) === false) return;
  const theme = useTheme();
  const {
    id = "panel1a-header",
    ariaControls = "panel1a-content",
    backgroundColor = "#ffffff",
    label = "Add label",
    applicantName = null,
    elevation = 0,
    maxWidth = { xs: "-webkit-fill-available", sm: "100%" },
    maxHeight = 76,
    width = { xs: 311, sm: 900 },
    contentWidth = { xs: "-webkit-fill-available", sm: `calc(100% - 50px)` },
    justifyContent = "space-between",
    labelVariant = "h6",
    stackMargin = { xs: 2, sm: 0, md: 5 },
    expanderColor = theme.palette.greyscale.Grey500,
    cardComponentType = "generic",
    isContactCenter = false,
    onBsbChange,
  } = { ...props };

  //----------------------------------------------------------------
  const paymentContext = useContext(PaymentContext);
  const { setPaymentMethodData, secureCoCapturedData, handleSetCaptureState } =
    paymentContext;
  //----------------------------------------------------------------

  const { dataSet, handleInputChange } = usePreQualification();

  const [selectedAccordion, setSelectedAccordion] = useState(null);

  const {
    errors: [...errors],
    methods: [methods],
    handlePaymentMethodType: [handlePaymentMethodType],
  } = useOutletContext();

  const { resetField } = { ...methods };

  const handleSwitchView = () => {
    //* When we switch between credit card and bank acc, clear the fields.
    try {
      resetField(`bankPaymentPermissionGranted`);
      resetField(`bankBSB`, "");
      resetField(`bankAccountNumber`, "");
      resetField(`bankAccountName`, "");
      resetField(`bankNameOfFinancialInstitution`, "");
    } catch (e) {
      console.log("Error ", e);
    }
  };

  const handleChange = (value) => {
    handleSwitchView();
    setSelectedAccordion(value);
    setPaymentMethodData(value === "creditcard" ? "CC" : "BA");
    handleInputChange({
      name: "isBankAccountPayment",
      value: value === "bankacc" ? true : false,
    });
    handlePaymentMethodType(value);
  };

  const [showGrantBankPermissionError, setShowGrantBankPermissionError] =
    useState(false);

  useEffect(() => {
    if (errors?.length <= 0) return;

    const outputBankPermissionQuestion =
      paymentDetailsFieldNames().BANK_PAYMENT_PERMISSION_GRANTED in errors[0];
    setShowGrantBankPermissionError(outputBankPermissionQuestion);
  }, [errors]);

  const fadeIn = 600;
  const captured = secureCoCapturedData?.captured;

  // const tempData = {
  //   captured: true,
  //   transactionNumber: 12000001651210,
  //   nonce: "fake-valid-visa-nonce",
  //   cardType: "Visa",
  //   maskedCardNumber: "401288******1881",
  //   cardHolderName: "Robert Smith",
  // };

  const [isModalShowing, setIsModalShowing] = useState(false);
  const [recaptured, setRecaptured] = useState(false);

  const handleOnClose = () => {
    setIsModalShowing(false);
    setRecaptured(false);
  };

  const handleOnOpen = () => {
    setIsModalShowing(true);
    handleSetCaptureState();
    setRecaptured(true);
  };

  const handOnBsbChange = (status) => {
    onBsbChange(status);
  };
  //? ----------------------------------------------------------------
  return isContactCenter === true ? (
    <>
      <AccordionContainer
        TransitionProps={{ unmountOnExit: true }}
        expanded={selectedAccordion === "creditcard"}
        // expanded={true}
        square
        disableGutters
        elevation={elevation}
        sx={{
          width: maxWidth,
          borderTop: { xs: "1px solid rgba(112,112,112, 0.5)", md: "initial" },
          borderBottom: { xs: "1px solid rgba(44,44,44, 0.36)" },
        }}
      >
        <AccordionSummary
          aria-controls={ariaControls}
          id={id}
          sx={{
            outline: "0px solid black",
            mx: { xs: 0, sm: 0 },
            backgroundColor: backgroundColor,
            // minWidth: width,
            width: "-webkit-fill-available",
            maxHeight: { maxHeight },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 1,
            pl: 0,
          }}
          onClick={() => handleChange("creditcard")}
        >
          <Stack
            sx={{ outline: "0px solid black" }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            pl={0}
          >
            <Radio
              value="creditcard"
              checked={selectedAccordion === "creditcard"}
              onChange={() => handleChange("creditcard")}
            />
            <Typography variant="h6">Debit or Credit Card</Typography>
          </Stack>
        </AccordionSummary>
        <Fade in={selectedAccordion === "creditcard"} timeout={fadeIn}>
          <div>
            <AccordionDetails
              sx={{
                outline: "0px solid red",
                width: width,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                pt: 0,
              }}
            >
              <Section
                sx={{
                  maxWidth: { xs: 636 },
                  width: "100%",
                  outline: "0px solid red",
                }}
              >
                {!captured ? (
                  <SecureCoModal
                    showing={isModalShowing}
                    handleOnClose={handleOnClose}
                    recapture={recaptured}
                  />
                ) : (
                  <CreditCardDetails
                    resetAll={true}
                    openModalOnRecapture={handleOnOpen}
                  />
                )}
              </Section>
            </AccordionDetails>
          </div>
        </Fade>
      </AccordionContainer>
      <AccordionContainer
        TransitionProps={{ unmountOnExit: true }}
        expanded={selectedAccordion === "bankacc"}
        square
        disableGutters
        elevation={elevation}
        sx={{
          width: maxWidth,
          borderTop: { xs: "1px solid rgba(112,112,112, 0.5)", md: "initial" },
          borderBottom: { xs: "0px solid rgba(44,44,44, 0.36)" },
        }}
      >
        <AccordionSummary
          aria-controls={ariaControls}
          id={id}
          sx={{
            mx: { xs: 0, sm: 0 },
            backgroundColor: backgroundColor,
            // minWidth: width,
            width: "-webkit-fill-available",
            maxHeight: { maxHeight },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: { justifyContent },
            py: 1,
            pl: 0,
          }}
          onClick={() => handleChange("bankacc")}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            pl={0}
          >
            <Radio
              value="bankacc"
              checked={selectedAccordion === "bankacc"}
              onChange={() => handleChange("bankacc")}
            />
            <Typography variant="h6">Bank account</Typography>
          </Stack>
        </AccordionSummary>

        <Fade in={selectedAccordion === "bankacc"} timeout={fadeIn}>
          <div>
            <AccordionDetails
              sx={{
                outline: "0px solid red",
                width: width,
                display: "flex",
                flexDirection: "column",
                margin: "0 auto",
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <OneColumnStack
                data-identifier="bank-payment-details-grant-permission-button"
                sx={{ maxWidth: { xs: 636 } }}
              >
                <ToggleButtonNotification
                  dataSetItem={dataSet.bankPaymentPermissionGranted}
                  fieldName={
                    paymentDetailsFieldNames().BANK_PAYMENT_PERMISSION_GRANTED
                  }
                  label={questionLabels.BANK_GRANT_PERMISSIONS_QUESTION}
                  items={questionTypes.YES_NO}
                  notificationCard={notificationCardItems(
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.cardType,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.title,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.message,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.additionalText
                  )}
                  showNotification={showGrantBankPermissionError}
                  showTooltip={false}
                  errorVariant={showGrantBankPermissionError}
                  labelVariant="p1"
                  position="start"
                />
              </OneColumnStack>
              <Section
                sx={{
                  maxWidth: { xs: 636 },
                  width: "100%",
                  outline: "0px solid red",
                }}
              >
                <RHFBankAccDetails
                  name="bankAccountDetails"
                  dataSet={dataSet}
                  resetAll={false}
                  onBsbChange={handOnBsbChange}
                  {...props}
                />
              </Section>
            </AccordionDetails>
          </div>
        </Fade>
      </AccordionContainer>
    </>
  ) : (
    <>
      <PageLoadingBackdrop />
      <AccordionContainer
        TransitionProps={{ unmountOnExit: true }}
        expanded={selectedAccordion === "creditcard"}
        // expanded={true}
        square
        disableGutters
        elevation={elevation}
        sx={{
          width: maxWidth,
          borderTop: { xs: "1px solid rgba(112,112,112, 0.5)", md: "initial" },
          borderBottom: { xs: "1px solid rgba(44,44,44, 0.36)" },
        }}
      >
        <AccordionSummary
          // expandIcon={<ExpandMoreIcon style={{ color: expanderColor }} />}
          aria-controls={ariaControls}
          id={id}
          sx={{
            outline: "0px solid black",
            mx: { xs: 0, sm: 0 },
            backgroundColor: backgroundColor,
            // minWidth: width,
            width: "-webkit-fill-available",
            maxHeight: { maxHeight },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 1,
            pl: 0,
            // outline: "0px solid red",
            // width: "636px",
            // sx={{ outline: "1px solid red" }}
          }}
          onClick={() => handleChange("creditcard")}
        >
          <Stack
            sx={{ outline: "0px solid black" }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            pl={0}
          >
            <Radio
              value="creditcard"
              checked={selectedAccordion === "creditcard"}
              onChange={() => handleChange("creditcard")}
            />
            <Typography variant="h6">Debit or Credit Card</Typography>
          </Stack>
        </AccordionSummary>
        <Fade in={selectedAccordion === "creditcard"} timeout={fadeIn}>
          <div>
            <AccordionDetails
              sx={{
                outline: "0px solid red",
                width: width,
                display: "flex",
                flexDirection: "column",
                // margin: "0 auto",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                pt: 0,
              }}
            >
              <Section
                sx={{
                  maxWidth: { xs: 636 },
                  width: "100%",
                  outline: "0px solid red",
                }}
              >
                <CreditDebitCard />
              </Section>
            </AccordionDetails>
          </div>
        </Fade>
      </AccordionContainer>
      <AccordionContainer
        TransitionProps={{ unmountOnExit: true }}
        expanded={selectedAccordion === "bankacc"}
        square
        disableGutters
        elevation={elevation}
        sx={{
          width: maxWidth,
          borderTop: { xs: "1px solid rgba(112,112,112, 0.5)", md: "initial" },
          borderBottom: { xs: "0px solid rgba(44,44,44, 0.36)" },
        }}
      >
        <AccordionSummary
          aria-controls={ariaControls}
          id={id}
          sx={{
            mx: { xs: 0, sm: 0 },
            backgroundColor: backgroundColor,
            // minWidth: width,
            width: "-webkit-fill-available",
            maxHeight: { maxHeight },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: { justifyContent },
            py: 1,
            pl: 0,
          }}
          onClick={() => handleChange("bankacc")}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            pl={0}
          >
            <Radio
              value="bankacc"
              checked={selectedAccordion === "bankacc"}
              onChange={() => handleChange("bankacc")}
            />
            <Typography variant="h6">Bank account</Typography>
          </Stack>
        </AccordionSummary>

        <Fade in={selectedAccordion === "bankacc"} timeout={fadeIn}>
          <div>
            <AccordionDetails
              sx={{
                outline: "0px solid red",
                width: width,
                display: "flex",
                flexDirection: "column",
                margin: "0 auto",
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <OneColumnStack
                data-identifier="bank-payment-details-grant-permission-button"
                sx={{ maxWidth: { xs: 636 } }}
              >
                <ToggleButtonNotification
                  dataSetItem={dataSet.bankPaymentPermissionGranted}
                  fieldName={
                    paymentDetailsFieldNames().BANK_PAYMENT_PERMISSION_GRANTED
                  }
                  label={questionLabels.BANK_GRANT_PERMISSIONS_QUESTION}
                  items={questionTypes.YES_NO}
                  notificationCard={notificationCardItems(
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.cardType,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.title,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.message,
                    paymentDetailsNotification
                      .GRANT_PERMISSION_DENY_NOTIFICATION.additionalText
                  )}
                  showNotification={showGrantBankPermissionError}
                  showTooltip={false}
                  errorVariant={showGrantBankPermissionError}
                  labelVariant="p1"
                  position="start"
                />
              </OneColumnStack>
              <Section
                sx={{
                  maxWidth: { xs: 636 },
                  width: "100%",
                  outline: "0px solid red",
                }}
              >
                <RHFBankAccDetails
                  name="bankAccountDetails"
                  dataSet={dataSet}
                  resetAll={false}
                  onBsbChange={handOnBsbChange}
                  {...props}
                />
              </Section>
            </AccordionDetails>
          </div>
        </Fade>
      </AccordionContainer>
    </>
  );
}
